// Medications — lightweight personal-tracker for scheduled meds (e.g. weekly
// Mounjaro injection). Owns its own localStorage key so it can live alongside
// Ghidha without touching the calorieLog schema.
//
// Surfaces two UI pieces:
//   • <MedicationsDueRow/>  — slim banner shown beneath the Today intel
//     strip when a dose is due or overdue.
//   • <MedicationsCard/>    — full list with add / edit / delete / log,
//     embedded in the Log view.
//
// Data model:
//   { id, name, emoji, dose, unit, frequency: 'daily'|'weekly',
//     dayOfWeek: 0–6 (only weekly), startDate: 'YYYY-MM-DD',
//     // Optional click-based pen dosing (e.g. Mounjaro multidose vials):
//     clicksPer: number, mgPer: number  (e.g. 15 clicks → 2.5 mg)
//     history: [{ ts: epochMs, dose: string, clicks?: number, unit?: string, notes?: string }] }

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { hapticImpact, hapticSelection } from "../haptics";

// ── Pen-click effect ───────────────────────────────────────────────────────
// Pre-rendered noise buffer + reusable BiquadFilter — generating a fresh
// AudioBuffer per tap created audible lag on iOS (alloc + GC churn).
let _audioCtx   = null;
let _clickBuf   = null;
let _filterNode = null;
function getAudioCtx() {
  if (_audioCtx) return _audioCtx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _audioCtx = new Ctx();
    return _audioCtx;
  } catch { return null; }
}
function ensureClickAssets() {
  const ctx = getAudioCtx();
  if (!ctx) return null;
  if (!_clickBuf) {
    const dur = 0.012;
    const buf = ctx.createBuffer(1, Math.max(1, Math.ceil(ctx.sampleRate * dur)), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const env = 1 - i / data.length;
      data[i] = (Math.random() * 2 - 1) * env * 0.6;
    }
    _clickBuf = buf;
  }
  if (!_filterNode) {
    _filterNode = ctx.createBiquadFilter();
    _filterNode.type = "bandpass";
    _filterNode.frequency.value = 4200;
    _filterNode.Q.value = 1.4;
    _filterNode.connect(ctx.destination);
  }
  return ctx;
}
function playPenClick() {
  const ctx = ensureClickAssets();
  if (!ctx || !_clickBuf || !_filterNode) return;
  if (ctx.state === "suspended") { try { ctx.resume(); } catch {} }
  // BufferSources are single-use, but the underlying AudioBuffer + filter
  // graph stay hot — wiring up a new source per tap is sub-millisecond.
  const src = ctx.createBufferSource();
  src.buffer = _clickBuf;
  const gain = ctx.createGain();
  gain.gain.value = 0.6;
  src.connect(gain).connect(_filterNode);
  src.start();
  src.stop(ctx.currentTime + 0.04);
}
function fireClickFeel() {
  // Audio first so the buzz doesn't delay it (iOS haptic engine has higher
  // latency than WebAudio).
  try { playPenClick(); } catch {}
  try { hapticSelection(); } catch {}
}

// ── Swipe-wheel control ───────────────────────────────────────────────────
// Drag VERTICALLY with your thumb to dial the clicks up or down — matches
// the way you'd twist a real pen's cylinder. Drag down = +1 (the indicator
// climbs as the wheel rolls toward you). Each STEP_PX of movement = one
// click; each step fires a click feel + invokes onStep(±1). Heavier
// hapticImpact("medium") fires at every multiple of `milestoneEvery` so
// the user feels the firmer detents on a real pen (e.g. every 15 clicks
// on Mounjaro = one whole-mg increment).
function ClickWheel({ value, accent, onStep, milestoneEvery, theme, ariaLabel }) {
  const { text, muted, border, darkMode } = theme;
  const ref = useRef(null);
  // Smaller pixels-per-step → faster click increments per pixel of drag.
  const STEP_PX = 4;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let startY = 0;
    let lastSteps = 0;
    let active = false;

    const begin = (y) => { active = true; startY = y; lastSteps = 0; };
    const move = (y, e) => {
      if (!active) return;
      // Drag DOWN → positive dy → increment. Feels like rolling the wheel
      // toward yourself; the value above your thumb climbs.
      const dy = y - startY;
      const steps = Math.trunc(dy / STEP_PX);
      const delta = steps - lastSteps;
      if (delta !== 0) {
        const dir = delta > 0 ? 1 : -1;
        const n = Math.abs(delta);
        for (let i = 0; i < n; i++) onStep(dir);
        lastSteps = steps;
        if (e && e.cancelable) e.preventDefault();
      }
    };
    const end = () => { active = false; lastSteps = 0; };

    const onTouchStart = (e) => begin(e.touches[0].clientY);
    const onTouchMove  = (e) => move(e.touches[0].clientY, e);
    const onTouchEnd   = () => end();
    const onMouseDown  = (e) => begin(e.clientY);
    const onMouseMove  = (e) => active && move(e.clientY, null);
    const onMouseUp    = () => end();

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    el.addEventListener("mousedown",  onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup",   onMouseUp);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      el.removeEventListener("mousedown",  onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup",   onMouseUp);
    };
  }, [onStep]);

  // Vertical tick marks — the strip slides up/down as the value changes,
  // giving the wheel a "rolling cylinder" affordance. Major ticks at the
  // milestone boundary (clicksPer for the dose dial → whole-mg detents).
  const ticks = [];
  for (let i = -8; i <= 8; i++) ticks.push(i);

  return (
    <div
      ref={ref}
      role="slider"
      aria-label={ariaLabel || "Dial clicks"}
      aria-valuenow={value}
      style={{
        position: "relative",
        width: 160, height: 200,
        margin: "0 auto",
        borderRadius: 22,
        background: darkMode
          ? "linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.18) 100%)"
          : "linear-gradient(90deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.12) 100%)",
        border: `0.5px solid ${border}`,
        cursor: "ns-resize",
        touchAction: "pan-x",
        WebkitUserSelect: "none",
        userSelect: "none",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "inset 1px 0 0 rgba(255,255,255,0.05), inset -1px 0 0 rgba(0,0,0,0.18)",
      }}
    >
      {/* Vertical tick marks — slide upward as value increments. */}
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, transform: `translateY(${-((value || 0) * 9) % 14}px)`, opacity: 0.32 }}>
          {ticks.map(i => {
            const tickValue = (value || 0) + i;
            const isMajor = milestoneEvery > 0 && (tickValue % milestoneEvery === 0);
            return (
              <div key={i} style={{
                width: isMajor ? 56 : 30,
                height: 1.5,
                background: isMajor ? accent : muted,
                borderRadius: 1,
              }}/>
            );
          })}
        </div>
      </div>
      {/* Centered value pill */}
      <div style={{
        position: "relative", zIndex: 2,
        fontSize: 44, fontWeight: 900,
        color: accent,
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "-0.02em",
        textShadow: darkMode ? "0 2px 12px rgba(0,0,0,0.6)" : "0 1px 4px rgba(255,255,255,0.6)",
        padding: "10px 22px",
        background: darkMode ? "rgba(13,17,23,0.78)" : "rgba(255,255,255,0.78)",
        borderRadius: 14,
        border: `0.5px solid ${border}`,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        minWidth: 96,
        textAlign: "center",
      }}>
        {value}
      </div>
      {/* Hint chevrons (top/bottom) */}
      <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", color: muted, fontSize: 16, opacity: 0.5, pointerEvents: "none" }}>˅</div>
      <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", color: muted, fontSize: 16, opacity: 0.5, pointerEvents: "none" }}>˄</div>
    </div>
  );
}

const STORAGE_KEY = "sirat-medications";
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MS_PER_DAY = 86400000;

// Popular GLP-1 / weight-management injectables, surfaced in the editor's
// dropdown. `clicksPer` + `mgPer` are only set on multidose-vial pens
// (Mounjaro, Zepbound) where the user dials the dose by counting clicks;
// pre-loaded single-use pens (Ozempic, Wegovy) just take a mg value.
const GLP1_PRESETS = [
  { id: "mounjaro",  name: "Mounjaro",  emoji: "💉", defaultDose: "5",     unit: "mg", clicksPer: 15, mgPer: 2.5, frequency: "weekly" },
  { id: "zepbound",  name: "Zepbound",  emoji: "💉", defaultDose: "5",     unit: "mg", clicksPer: 15, mgPer: 2.5, frequency: "weekly" },
  { id: "ozempic",   name: "Ozempic",   emoji: "💉", defaultDose: "0.5",   unit: "mg", clicksPer: 0,  mgPer: 0,   frequency: "weekly" },
  { id: "wegovy",    name: "Wegovy",    emoji: "💉", defaultDose: "0.25",  unit: "mg", clicksPer: 0,  mgPer: 0,   frequency: "weekly" },
  { id: "trulicity", name: "Trulicity", emoji: "💉", defaultDose: "0.75",  unit: "mg", clicksPer: 0,  mgPer: 0,   frequency: "weekly" },
  { id: "saxenda",   name: "Saxenda",   emoji: "💉", defaultDose: "0.6",   unit: "mg", clicksPer: 0,  mgPer: 0,   frequency: "daily"  },
  { id: "rybelsus",  name: "Rybelsus",  emoji: "💊", defaultDose: "7",     unit: "mg", clicksPer: 0,  mgPer: 0,   frequency: "daily"  },
  { id: "victoza",   name: "Victoza",   emoji: "💉", defaultDose: "1.2",   unit: "mg", clicksPer: 0,  mgPer: 0,   frequency: "daily"  },
];
function findGlp1Preset(name) {
  const t = (name || "").toLowerCase().trim();
  if (!t) return null;
  return GLP1_PRESETS.find(p => t.includes(p.id)) || null;
}

export function loadMeds() {
  let raw;
  try { raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; }
  if (!Array.isArray(raw)) return [];
  // Migration v2: any GLP-1 with a known preset name has its ratio
  // (clicksPer / mgPer) FORCED back to the canonical preset values. v1
  // only filled missing ratios; older saves with a wrong-but-non-zero
  // ratio (e.g. 20 clicks per 2.5 mg on Mounjaro) stayed broken. v2
  // overrides those to the correct preset (Mounjaro = 15 / 2.5).
  let mutated = false;
  const next = raw.map(m => {
    if (!m) return m;
    const preset = findGlp1Preset(m.name);
    if (!preset || preset.clicksPer === 0) return m;
    const ratioMatches = m.clicksPer === preset.clicksPer && m.mgPer === preset.mgPer;
    if (ratioMatches) return m;
    mutated = true;
    return { ...m, clicksPer: preset.clicksPer, mgPer: preset.mgPer };
  });
  if (mutated) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  }
  return next;
}
export function saveMeds(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch {}
}
export function newMedId() {
  return `med-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

// ── Click ↔ mg conversion ──────────────────────────────────────────────────
// A med opts into click-based dosing by setting both clicksPer and mgPer
// (e.g. Mounjaro multidose vials: 15 clicks → 2.5 mg). When configured,
// `mgPerClick` is mgPer / clicksPer (rounded sensibly) and we round to one
// decimal on mg, integers on clicks.

export function hasClickConfig(med) {
  return !!(med && med.clicksPer > 0 && med.mgPer > 0);
}
export function mgPerClick(med) {
  if (!hasClickConfig(med)) return 0;
  return med.mgPer / med.clicksPer;
}
export function clicksToMg(med, clicks) {
  if (!hasClickConfig(med)) return null;
  const c = parseFloat(clicks);
  if (!isFinite(c)) return null;
  return Math.round((c * mgPerClick(med)) * 100) / 100;
}
export function mgToClicks(med, mg) {
  if (!hasClickConfig(med)) return null;
  const m = parseFloat(mg);
  if (!isFinite(m) || mgPerClick(med) === 0) return null;
  return Math.round(m / mgPerClick(med));
}

// Returns { date: Date, dueIn: 'today'|'overdue'|'soon'|'later', daysAway: int }
// for the next dose. Weekly meds use dayOfWeek; daily meds advance by 1 day
// from the last log (or from today if none).
export function nextDose(med) {
  const now = new Date();
  const last = (med.history || [])[med.history.length - 1];
  const lastTs = last ? last.ts : null;

  let nextDate;
  if (med.frequency === "weekly") {
    const target = typeof med.dayOfWeek === "number" ? med.dayOfWeek : now.getDay();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let daysToTarget = (target - today.getDay() + 7) % 7;
    // If logged today already, jump a week ahead.
    if (lastTs && sameLocalDay(new Date(lastTs), today)) {
      daysToTarget = daysToTarget === 0 ? 7 : daysToTarget;
    }
    // If today IS the target day but not yet logged, daysToTarget is 0 (due today).
    // If we logged on the target day last week, daysToTarget = 0 which is wrong — but the
    // sameLocalDay branch above already pushed it.
    nextDate = new Date(today.getTime() + daysToTarget * MS_PER_DAY);
  } else {
    // daily
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (lastTs && sameLocalDay(new Date(lastTs), today)) {
      nextDate = new Date(today.getTime() + MS_PER_DAY);
    } else {
      nextDate = today;
    }
  }
  const startMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const daysAway = Math.round((nextDate.getTime() - startMs) / MS_PER_DAY);
  let dueIn;
  if (daysAway < 0) dueIn = "overdue";
  else if (daysAway === 0) dueIn = "today";
  else if (daysAway <= 3) dueIn = "soon";
  else dueIn = "later";
  return { date: nextDate, daysAway, dueIn };
}

function sameLocalDay(a, b) {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}

function fmtDueLabel(nd) {
  if (nd.dueIn === "today")   return "Due today";
  if (nd.dueIn === "overdue") return `Overdue · ${Math.abs(nd.daysAway)}d`;
  if (nd.daysAway === 1)      return "Due tomorrow";
  return `Due ${DAY_SHORT[nd.date.getDay()]} · in ${nd.daysAway}d`;
}

// Append a dose entry (mg + optional clicks) to a med's history. Used by both
// surface points so the history shape stays consistent. Broadcasts a
// `medications-updated` event so other mounted views (Log card / Today
// due-row) refresh from localStorage instead of holding stale state.
function appendDoseToMed(medId, entry) {
  // Persist the most recent dose / unit on the med object itself so the
  // visible GLP-1 block reflects what the user actually dialled in. Without
  // this the row kept showing the original prescribed dose forever, even
  // after logging a different click count.
  const next = loadMeds().map(m => m.id !== medId ? m : ({
    ...m,
    dose: entry.dose != null && entry.dose !== '' ? String(entry.dose) : m.dose,
    unit: entry.unit || m.unit,
    history: [...(m.history || []), { ts: Date.now(), ...entry }],
  }));
  saveMeds(next);
  try { window.dispatchEvent(new CustomEvent("medications-updated")); } catch {}
  return next;
}

// ── Today-screen due banner ─────────────────────────────────────────────────
export function MedicationsDueRow({ theme, onLog }) {
  const { gold, teal, danger, text, muted, border, s1, s2, darkMode } = theme;
  const [meds, setMeds] = useState(() => loadMeds());
  const [logging, setLogging] = useState(null); // med | null

  // Refresh when storage changes from elsewhere — same-window edits via
  // the medications-updated CustomEvent (from appendDoseToMed/saveMeds),
  // cross-window via the native storage event.
  useEffect(() => {
    const refresh = () => setMeds(loadMeds());
    window.addEventListener("storage", refresh);
    window.addEventListener("medications-updated", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("medications-updated", refresh);
    };
  }, []);

  const due = meds
    .map(m => ({ med: m, nd: nextDose(m) }))
    .filter(({ nd }) => nd.dueIn === "today" || nd.dueIn === "overdue" || nd.dueIn === "soon")
    .sort((a, b) => a.nd.daysAway - b.nd.daysAway);
  if (due.length === 0) return null;

  const handleSubmit = (entry) => {
    const next = appendDoseToMed(logging.id, entry);
    setMeds(next);
    setLogging(null);
    if (onLog) onLog(logging);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {due.map(({ med, nd }) => {
        const accent = nd.dueIn === "overdue" ? danger : nd.dueIn === "today" ? gold : teal;
        const clicksLabel = hasClickConfig(med) ? ` · ${mgToClicks(med, med.dose)} clicks` : "";
        return (
          <div key={med.id} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 11px",
            borderRadius: 12,
            background: darkMode ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.025)",
            border: `0.5px solid ${border}`,
          }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>{med.emoji || "💊"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: text, fontWeight: 700, letterSpacing: "-0.005em" }}>
                {med.name} · <span style={{ color: muted, fontWeight: 600 }}>{med.dose}{med.unit}{clicksLabel}</span>
              </div>
              <div style={{ fontSize: 10, color: accent, fontFamily: "monospace", letterSpacing: "0.08em", fontWeight: 700, marginTop: 1 }}>
                {fmtDueLabel(nd).toUpperCase()}
              </div>
            </div>
            <button onClick={() => setLogging(med)}
              style={{
                background: accent, color: "#0A0A0E", border: 0, borderRadius: 999,
                padding: "5px 12px", fontSize: 11, fontWeight: 800, cursor: "pointer",
                fontFamily: "inherit", flexShrink: 0,
              }}>Log</button>
          </div>
        );
      })}
      {logging && createPortal(
        <LogDoseSheet theme={{ gold, teal, text, muted, border, s1, s2, darkMode }}
          med={logging}
          onSubmit={handleSubmit}
          onClose={() => setLogging(null)}/>,
        document.body)}
    </div>
  );
}

// ── Log-view list ───────────────────────────────────────────────────────────
export function MedicationsCard({ theme, showToast }) {
  const { gold, teal, text, muted, border, s1, s2, darkMode } = theme;
  const [meds, setMeds] = useState(() => loadMeds());
  const [editing, setEditing] = useState(null); // med | "new" | null

  // Sync from localStorage when meds change anywhere — covers logging from
  // the Today due-row while the Log card is mounted, plus cross-window.
  useEffect(() => {
    const refresh = () => setMeds(loadMeds());
    window.addEventListener("storage", refresh);
    window.addEventListener("medications-updated", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("medications-updated", refresh);
    };
  }, []);

  const persist = (next) => {
    saveMeds(next);
    setMeds(next);
    try { window.dispatchEvent(new CustomEvent("medications-updated")); } catch {}
  };
  const handleSave = (med) => {
    const list = loadMeds();
    const i = list.findIndex(m => m.id === med.id);
    const next = i >= 0 ? list.map(m => m.id === med.id ? med : m) : [...list, med];
    persist(next);
    setEditing(null);
    if (showToast) showToast(i >= 0 ? "GLP-1 updated" : "GLP-1 added");
  };
  const handleDelete = (id) => {
    persist(loadMeds().filter(m => m.id !== id));
    setEditing(null);
    if (showToast) showToast("GLP-1 removed");
  };
  const [logging, setLogging] = useState(null); // med | null
  const submitDose = (entry) => {
    const next = appendDoseToMed(logging.id, entry);
    persist(next);
    if (showToast) {
      const tail = entry.clicks != null ? ` (${entry.clicks} clicks)` : "";
      showToast(`Logged ${logging.name} ${entry.dose}${logging.unit}${tail}`);
    }
    setLogging(null);
  };

  return (
    <div style={{ padding: "0 16px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: muted, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase" }}>💉 GLP-1</div>
        <button onClick={() => setEditing("new")}
          style={{ background: "transparent", border: `0.5px solid ${border}`, color: gold, fontSize: 11, fontWeight: 700, padding: "5px 10px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>+ Add</button>
      </div>

      {meds.length === 0 ? (
        <div style={{ padding: "16px 14px", textAlign: "center", fontSize: 12, color: muted,
          background: s1, border: `0.5px dashed ${border}`, borderRadius: 12 }}>
          No GLP-1 added yet. Pick from Mounjaro, Ozempic, Wegovy, Zepbound, Trulicity, Saxenda, Rybelsus or Victoza.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {meds.map(med => {
            const nd = nextDose(med);
            const last = (med.history || [])[med.history.length - 1];
            const lastLabel = last
              ? new Date(last.ts).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
              : "Never";
            return (
              <div key={med.id} style={{ background: s1, border: `0.5px solid ${border}`, borderRadius: 12, padding: "10px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 22, lineHeight: 1 }}>{med.emoji || "💊"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: text, fontWeight: 700 }}>{med.name}</div>
                    <div style={{ fontSize: 10.5, color: muted, fontFamily: "monospace", letterSpacing: "0.06em", marginTop: 1 }}>
                      {med.dose}{med.unit}
                      {hasClickConfig(med) && ` · ${mgToClicks(med, med.dose)} clicks`}
                      {" · "}{med.frequency === "weekly" ? `Weekly · ${DAY_NAMES[med.dayOfWeek ?? 0]}` : "Daily"}
                    </div>
                  </div>
                  <button onClick={() => setLogging(med)}
                    style={{ background: gold, color: "#0A0A0E", border: 0, borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 800, cursor: "pointer", flexShrink: 0 }}>Log</button>
                  <button onClick={() => setEditing(med)} aria-label="Edit"
                    style={{ background: "transparent", border: `0.5px solid ${border}`, color: muted, borderRadius: 8, padding: "4px 8px", fontSize: 11, cursor: "pointer", flexShrink: 0 }}>Edit</button>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8,
                  fontSize: 10, color: muted, fontFamily: "monospace", letterSpacing: "0.06em" }}>
                  <span>NEXT · <span style={{ color: nd.dueIn === "today" ? gold : nd.dueIn === "overdue" ? "#e57373" : teal, fontWeight: 700 }}>{fmtDueLabel(nd).toUpperCase()}</span></span>
                  <span>LAST · {lastLabel.toUpperCase()}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && createPortal(
        <MedicationEditSheet theme={theme} med={editing === "new" ? null : editing}
          onSave={handleSave} onDelete={handleDelete}
          onClose={() => setEditing(null)} />,
        document.body)}

      {logging && createPortal(
        <LogDoseSheet theme={theme} med={logging}
          onSubmit={submitDose}
          onClose={() => setLogging(null)} />,
        document.body)}
    </div>
  );
}

function MedicationEditSheet({ theme, med, onSave, onDelete, onClose }) {
  const { gold, text, muted, border, s1, s2, darkMode } = theme;
  // Initial preset: match by existing name when editing, default to Mounjaro
  // for a brand-new entry — that's the most common case for this user.
  const initialPreset = med ? findGlp1Preset(med.name) : GLP1_PRESETS[0];
  const [presetId, setPresetId]   = useState(initialPreset?.id || GLP1_PRESETS[0].id);
  const [name, setName]           = useState(med?.name  || initialPreset?.name  || GLP1_PRESETS[0].name);
  const [emoji, setEmoji]         = useState(med?.emoji || initialPreset?.emoji || "💉");
  const [dose, setDose]           = useState(med?.dose  || initialPreset?.defaultDose || "");
  const [unit, setUnit]           = useState(med?.unit  || initialPreset?.unit || "mg");
  const [frequency, setFreq]      = useState(med?.frequency || initialPreset?.frequency || "weekly");
  const [dayOfWeek, setDow]       = useState(med?.dayOfWeek ?? new Date().getDay());
  const initialUseClicks = med?.clicksPer != null ? !!med.clicksPer : (initialPreset?.clicksPer > 0);
  const [useClicks, setUseClicks] = useState(initialUseClicks);
  // Preset values WIN over the saved med values when a known GLP-1 preset
  // matches by name. This fixes stale meds saved with the wrong ratio
  // (e.g. Mounjaro saved at 20:2.5 instead of 15:2.5) — opening the editor
  // now always shows the canonical ratio for known products. The user can
  // still toggle pen-clicks off if they want a custom ratio.
  const presetForcesRatio = !!(initialPreset && initialPreset.clicksPer > 0 && initialPreset.mgPer > 0);
  const [clicksPer, setClicksPer] = useState(presetForcesRatio ? initialPreset.clicksPer : (med?.clicksPer ?? 15));
  const [mgPer, setMgPer]         = useState(presetForcesRatio ? initialPreset.mgPer    : (med?.mgPer    ?? 2.5));
  const editorMilestoneRef = useRef(-1);

  // Picking from the dropdown rewrites every field to that preset's defaults
  // so a single tap is enough to add a new GLP-1.
  const applyPreset = (id) => {
    const p = GLP1_PRESETS.find(x => x.id === id);
    if (!p) return;
    setPresetId(id);
    setName(p.name);
    setEmoji(p.emoji);
    setDose(p.defaultDose);
    setUnit(p.unit);
    setFreq(p.frequency || "weekly");
    setUseClicks(p.clicksPer > 0);
    if (p.clicksPer > 0) {
      setClicksPer(p.clicksPer);
      setMgPer(p.mgPer);
    }
  };

  // Vertical click-wheel for the editor's clicks-per-dose-unit value. Each
  // step fires a click feel so configuring the ratio feels like dialling a
  // real pen; heavier impact every 5 clicks for progress.
  const stepEditorClicks = (dir) => {
    const cur = parseFloat(clicksPer);
    const next = Math.max(1, (isFinite(cur) ? Math.round(cur) : 0) + dir);
    setClicksPer(next);
    const milestone = Math.floor(next / 5);
    if (milestone !== editorMilestoneRef.current && next > 0) {
      try { hapticImpact("medium"); } catch {}
      editorMilestoneRef.current = milestone;
    } else {
      fireClickFeel();
    }
  };

  // Save lights up as soon as a name + dose are set — both are pre-filled
  // by the preset so it's enabled the moment the sheet opens.
  const canSave = !!(name && name.trim() && dose && String(dose).trim());
  const submit = () => {
    if (!canSave) return;
    const cp = parseFloat(clicksPer);
    const mp = parseFloat(mgPer);
    const out = {
      id: med?.id || newMedId(),
      name: name.trim(),
      emoji: emoji || "💊",
      dose: String(dose).trim(),
      unit: (unit || "mg").trim() || "mg",
      frequency,
      dayOfWeek: frequency === "weekly" ? Number(dayOfWeek) : null,
      startDate: med?.startDate || new Date().toISOString().slice(0, 10),
      clicksPer: useClicks && cp > 0 && mp > 0 ? cp : null,
      mgPer:     useClicks && cp > 0 && mp > 0 ? mp : null,
      history: med?.history || [],
    };
    onSave(out);
  };

  const overlay = {
    position: "fixed", inset: 0, zIndex: 9999,
    background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
    display: "flex", alignItems: "flex-end", justifyContent: "center",
  };
  const sheet = {
    width: "100%", maxWidth: 520,
    background: darkMode ? "#0d1117" : "#fff",
    borderTopLeftRadius: 18, borderTopRightRadius: 18,
    border: `0.5px solid ${border}`, padding: "16px 16px 22px",
    paddingBottom: "calc(22px + env(safe-area-inset-bottom))",
  };
  const inputBase = { width: "100%", fontSize: 14, padding: "10px 12px", background: s2, border: `0.5px solid ${border}`, borderRadius: 10, color: text, fontFamily: "inherit", boxSizing: "border-box" };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: text }}>
            {med ? "Edit GLP-1" : "Add GLP-1"}
          </div>
          <button onClick={onClose}
            style={{ background: s1, border: `0.5px solid ${border}`, borderRadius: 8, width: 28, height: 28, color: muted, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 9.5, color: muted, fontWeight: 700, letterSpacing: "0.14em", marginBottom: 6 }}>GLP-1 TYPE</div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ width: 50, height: 44, background: s2, border: `0.5px solid ${border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
              {emoji}
            </div>
            <select
              value={presetId}
              onChange={(e) => applyPreset(e.target.value)}
              style={{
                flex: 1, fontSize: 14, padding: "10px 12px",
                background: s2, border: `0.5px solid ${border}`, borderRadius: 10,
                color: text, fontFamily: "inherit", boxSizing: "border-box",
                fontWeight: 700, appearance: "none", WebkitAppearance: "none",
                backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%23${(muted || '#888888').replace('#','')}' d='M1 1.5l5 5 5-5'/></svg>")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                paddingRight: 32,
              }}>
              {GLP1_PRESETS.map(p => (
                <option key={p.id} value={p.id}>{p.emoji}  {p.name}</option>
              ))}
            </select>
          </div>
        </div>

        {useClicks && parseFloat(clicksPer) > 0 && parseFloat(mgPer) > 0 ? (
          // Dose-in-clicks dial drives the mg dose live via the current ratio
          // so the user can dial in clicks and see mg update in real time.
          <div style={{ marginBottom: 10, padding: "10px 12px", background: s1, border: `0.5px solid ${border}`, borderRadius: 12 }}>
            <div style={{ fontSize: 9.5, color: muted, fontWeight: 700, letterSpacing: "0.14em", marginBottom: 8, textAlign: "center" }}>
              DOSE · drag to dial clicks
            </div>
            <ClickWheel
              value={(() => {
                const cp = parseFloat(clicksPer); const mp = parseFloat(mgPer);
                const m = parseFloat(dose);
                if (!isFinite(m) || !isFinite(cp) || !isFinite(mp) || mp === 0) return 0;
                return Math.round(m / (mp / cp));
              })()}
              accent={gold}
              milestoneEvery={parseInt(clicksPer, 10) || 0}
              theme={{ text, muted, border, s1, darkMode }}
              ariaLabel="Dial dose in clicks"
              onStep={(dir) => {
                const cp = parseFloat(clicksPer); const mp = parseFloat(mgPer);
                if (!isFinite(cp) || !isFinite(mp) || mp === 0) return;
                const mgPerClickLocal = mp / cp;
                const cur = parseFloat(dose);
                const curClicks = Math.max(0, Math.round((isFinite(cur) ? cur : 0) / mgPerClickLocal));
                const next = Math.max(0, curClicks + dir);
                const nextMg = Math.round(next * mgPerClickLocal * 100) / 100;
                setDose(String(nextMg));
                const milestone = cp > 0 ? Math.floor(next / cp) : -1;
                if (milestone !== editorMilestoneRef.current && next > 0) {
                  try { hapticImpact("medium"); } catch {}
                  editorMilestoneRef.current = milestone;
                } else {
                  fireClickFeel();
                }
              }}
            />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 10, fontSize: 13, color: muted, fontFamily: "monospace" }}>
              <span>=</span>
              <input value={dose}
                onChange={(e) => setDose(e.target.value.replace(/[^0-9.]/g, ""))}
                inputMode="decimal" placeholder="5"
                style={{
                  width: 72, fontSize: 16, fontWeight: 800,
                  fontVariantNumeric: "tabular-nums",
                  textAlign: "center", padding: "8px 10px",
                  background: s2, border: `0.5px solid ${border}`,
                  borderRadius: 10, color: text, fontFamily: "inherit",
                  boxSizing: "border-box", outline: "none",
                }}/>
              <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="mg"
                style={{ width: 56, fontSize: 13, padding: "8px 10px", background: s2, border: `0.5px solid ${border}`, borderRadius: 10, color: text, fontFamily: "inherit", boxSizing: "border-box", textAlign: "center", outline: "none" }}/>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input value={dose} onChange={(e) => setDose(e.target.value)} placeholder="Dose (e.g. 5)"
              inputMode="decimal" style={{ ...inputBase, flex: 1 }}/>
            <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="mg"
              style={{ ...inputBase, width: 80 }}/>
          </div>
        )}

        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          {[{ k: "weekly", l: "Weekly" }, { k: "daily", l: "Daily" }].map(opt => (
            <button key={opt.k} onClick={() => setFreq(opt.k)}
              style={{
                flex: 1, padding: "9px 10px", borderRadius: 10, fontSize: 12, fontWeight: 700,
                background: frequency === opt.k ? gold : s2,
                color: frequency === opt.k ? "#0A0A0E" : text,
                border: `0.5px solid ${frequency === opt.k ? gold : border}`,
                cursor: "pointer", fontFamily: "inherit",
              }}>{opt.l}</button>
          ))}
        </div>

        {frequency === "weekly" && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: muted, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 6 }}>DAY</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
              {DAY_SHORT.map((d, i) => (
                <button key={i} onClick={() => setDow(i)}
                  style={{
                    padding: "8px 0", borderRadius: 8, fontSize: 11, fontWeight: 700,
                    background: Number(dayOfWeek) === i ? gold : s2,
                    color: Number(dayOfWeek) === i ? "#0A0A0E" : text,
                    border: `0.5px solid ${Number(dayOfWeek) === i ? gold : border}`,
                    cursor: "pointer", fontFamily: "inherit",
                  }}>{d}</button>
              ))}
            </div>
          </div>
        )}

        {/* Click-based pen dosing (Mounjaro multidose vials etc.) */}
        <div style={{ marginBottom: 14, padding: "10px 12px", background: s1, border: `0.5px solid ${border}`, borderRadius: 12 }}>
          <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, cursor: "pointer" }}>
            <div>
              <div style={{ fontSize: 12, color: text, fontWeight: 700 }}>Pen clicks</div>
              <div style={{ fontSize: 10.5, color: muted, marginTop: 1 }}>For multidose vials — log doses by clicks too</div>
            </div>
            <input type="checkbox" checked={useClicks}
              onChange={(e) => setUseClicks(e.target.checked)}
              style={{ width: 18, height: 18, cursor: "pointer", accentColor: gold }}/>
          </label>
          {useClicks && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 9.5, color: muted, fontWeight: 700, letterSpacing: "0.14em", marginBottom: 6, textAlign: "center" }}>
                CLICKS PER DOSE UNIT · drag to dial
              </div>
              <ClickWheel
                value={parseInt(clicksPer, 10) || 0}
                accent={gold}
                milestoneEvery={5}
                theme={{ text, muted, border, s1, darkMode }}
                ariaLabel="Dial clicks per dose unit"
                onStep={(dir) => stepEditorClicks(dir)}
              />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 10, fontSize: 12, color: muted }}>
                <span>=</span>
                <input value={mgPer}
                  onChange={(e) => setMgPer(e.target.value.replace(/[^0-9.]/g, ""))}
                  inputMode="decimal" placeholder="2.5"
                  style={{
                    width: 64, fontSize: 14, fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    textAlign: "center", padding: "8px 10px",
                    background: s2, border: `0.5px solid ${border}`,
                    borderRadius: 10, color: text, fontFamily: "inherit",
                    boxSizing: "border-box", outline: "none",
                  }}/>
                <span>{unit || "mg"}</span>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          {med && (
            <button onClick={() => onDelete(med.id)}
              style={{ background: "transparent", border: `0.5px solid rgba(217,122,106,0.3)`, color: "#e57373", borderRadius: 12, padding: "11px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Delete</button>
          )}
          <button onClick={onClose}
            style={{ flex: 1, background: s2, border: `0.5px solid ${border}`, color: text, borderRadius: 12, padding: "11px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={submit} disabled={!canSave}
            style={{
              flex: 1.4, background: canSave ? gold : s2,
              color: canSave ? "#0A0A0E" : muted, border: 0,
              borderRadius: 12, padding: "11px", fontSize: 13, fontWeight: 800,
              cursor: canSave ? "pointer" : "not-allowed", fontFamily: "inherit",
            }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── Log-dose bottom sheet ──────────────────────────────────────────────────
// Tapping "Log" anywhere opens this sheet with the med's saved default dose
// prefilled. Two linked inputs — mg and clicks — keep each other in sync via
// the med's clicksPer / mgPer ratio so the user can dial in either side
// (e.g. "15 clicks → 2.5 mg" or "20 clicks → 3.33 mg"). Falls back to a
// single mg input when click-config isn't set on the med.
function LogDoseSheet({ theme, med, onSubmit, onClose }) {
  const { gold, text, muted, border, s1, s2, darkMode } = theme;
  const ck = hasClickConfig(med);
  const initialMg     = med.dose || "";
  const initialClicks = ck ? String(mgToClicks(med, initialMg) ?? "") : "";
  const [mg, setMg]         = useState(initialMg);
  const [clicks, setClicks] = useState(initialClicks);
  const lastMilestoneRef = useRef(-1);
  // Ref-tracked clicks count drives the wheel — rapid drag events fire
  // multiple onStep calls in the same React tick, and reading `clicks` from
  // the state closure would be stale after the first tick. Bumping the ref
  // synchronously keeps every step (and its derived mg readout) advancing
  // from the real current value.
  const clicksRef = useRef(parseInt(initialClicks, 10) || 0);

  const onMgChange = (v) => {
    setMg(v);
    if (ck) {
      const n = mgToClicks(med, v);
      const s = n != null && isFinite(n) ? String(n) : "";
      clicksRef.current = parseInt(s, 10) || 0;
      setClicks(s);
    }
  };
  const onClicksChange = (v) => {
    clicksRef.current = parseInt(v, 10) || 0;
    setClicks(v);
    if (ck) {
      const n = clicksToMg(med, v);
      setMg(n != null && isFinite(n) ? String(n) : "");
    }
  };

  // ── Click stepper ─────────────────────────────────────────────────────────
  // Each step fires the pen-click feel and synchronously advances clicksRef
  // so the linked mg readout updates with every notch of the wheel. Heavier
  // impact at each clicksPer boundary (15 → 2.5 mg → firmer detent).
  const stepClicks = (dir) => {
    const next = Math.max(0, clicksRef.current + dir);
    clicksRef.current = next;
    setClicks(String(next));
    if (ck) {
      const m = clicksToMg(med, next);
      if (m != null && isFinite(m)) setMg(String(m));
    }
    const milestone = ck && med.clicksPer > 0 ? Math.floor(next / med.clicksPer) : -1;
    if (milestone !== lastMilestoneRef.current && next > 0) {
      try { hapticImpact("medium"); } catch {}
      lastMilestoneRef.current = milestone;
    } else {
      fireClickFeel();
    }
  };
  const submit = () => {
    // Compute the saved values defensively. If mg is empty for any reason
    // (e.g. a transient state during a wheel drag), derive it from clicks
    // using the med's ratio so the Log button never silently no-ops.
    let mgValue = (mg ?? "").toString().trim();
    let clicksValue = ck ? parseInt(clicks, 10) : NaN;
    if (!mgValue && ck && isFinite(clicksValue)) {
      const m = clicksToMg(med, clicksValue);
      if (m != null && isFinite(m)) mgValue = String(m);
    }
    if (!mgValue && ck && !isFinite(clicksValue)) {
      // Fall back to the med's saved default dose if neither side resolved.
      mgValue = String(med.dose || "");
    }
    if (!mgValue) return;
    const entry = { dose: mgValue, unit: med.unit || "mg" };
    if (ck && isFinite(clicksValue)) entry.clicks = clicksValue;
    onSubmit(entry);
  };

  const overlay = {
    position: "fixed", inset: 0, zIndex: 9999,
    background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
    display: "flex", alignItems: "flex-end", justifyContent: "center",
  };
  const sheet = {
    width: "100%", maxWidth: 520,
    background: darkMode ? "#0d1117" : "#fff",
    borderTopLeftRadius: 18, borderTopRightRadius: 18,
    border: `0.5px solid ${border}`, padding: "16px 16px 22px",
    paddingBottom: "calc(22px + env(safe-area-inset-bottom))",
  };
  const inputBase = { width: "100%", fontSize: 16, padding: "12px", background: s2, border: `0.5px solid ${border}`, borderRadius: 10, color: text, fontFamily: "inherit", boxSizing: "border-box", textAlign: "center", fontWeight: 700 };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: text }}>Log {med.name}</div>
            <div style={{ fontSize: 11, color: muted, marginTop: 2 }}>
              {ck ? `${med.clicksPer} clicks = ${med.mgPer}${med.unit || "mg"}` : "Adjust dose if needed"}
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: s1, border: `0.5px solid ${border}`, borderRadius: 8, width: 28, height: 28, color: muted, cursor: "pointer" }}>×</button>
        </div>

        {ck ? (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 9.5, color: muted, fontWeight: 700, letterSpacing: "0.14em", marginBottom: 8, textAlign: "center" }}>
              CLICKS · drag to dial
            </div>
            <ClickWheel
              value={parseInt(clicks, 10) || 0}
              accent={gold}
              milestoneEvery={med.clicksPer || 0}
              theme={{ text, muted, border, s1, darkMode }}
              ariaLabel="Dial dose clicks"
              onStep={(dir) => stepClicks(dir)}
            />
            <div style={{ textAlign: "center", marginTop: 10, fontSize: 13, color: muted, fontFamily: "monospace", letterSpacing: "0.06em" }}>
              ={" "}
              <input value={mg}
                onChange={(e) => onMgChange(e.target.value.replace(/[^0-9.]/g, ""))}
                inputMode="decimal"
                style={{
                  width: 64, fontSize: 14, fontWeight: 800,
                  fontVariantNumeric: "tabular-nums", color: text,
                  background: "transparent", border: 0, outline: "none",
                  textAlign: "right", fontFamily: "inherit",
                }}/>
              {" "}{med.unit || "mg"}
            </div>
            {/* Quick presets for common click totals at this med's ratio. */}
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
              {[
                { c: med.clicksPer, l: `${med.mgPer}${med.unit || "mg"}` },
                { c: med.clicksPer * 2, l: `${(med.mgPer * 2).toFixed(med.mgPer % 1 === 0 ? 0 : 1)}${med.unit || "mg"}` },
                { c: med.clicksPer * 3, l: `${(med.mgPer * 3).toFixed(med.mgPer % 1 === 0 ? 0 : 1)}${med.unit || "mg"}` },
              ].map((preset) => (
                <button key={preset.c}
                  onClick={() => { fireClickFeel(); onClicksChange(String(preset.c)); }}
                  style={{
                    padding: "6px 11px", borderRadius: 999,
                    background: parseInt(clicks, 10) === preset.c ? gold : s2,
                    border: `0.5px solid ${parseInt(clicks, 10) === preset.c ? gold : border}`,
                    color: parseInt(clicks, 10) === preset.c ? "#0A0A0E" : text,
                    fontSize: 11, fontWeight: 700, cursor: "pointer",
                    fontFamily: "inherit",
                  }}>{preset.c} · {preset.l}</button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 9.5, color: muted, fontWeight: 700, letterSpacing: "0.14em", marginBottom: 5 }}>{(med.unit || "mg").toUpperCase()}</div>
            <input value={mg} onChange={(e) => onMgChange(e.target.value)}
              inputMode="decimal" autoFocus style={inputBase}/>
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose}
            style={{ flex: 1, background: s2, border: `0.5px solid ${border}`, color: text, borderRadius: 12, padding: "11px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          {(() => {
            // Log button enabled when EITHER mg OR clicks resolves to a real
            // value. submit() bridges from clicks → mg if mg is empty, so
            // disabling on `!mg` alone was masking valid wheel-only doses.
            const canLog = !!((mg && mg.toString().trim()) || (ck && parseInt(clicks, 10) > 0));
            return (
          <button onClick={submit} disabled={!canLog}
            style={{
              flex: 1.4, background: canLog ? gold : s2,
              color: canLog ? "#0A0A0E" : muted, border: 0,
              borderRadius: 12, padding: "11px", fontSize: 13, fontWeight: 800,
              cursor: canLog ? "pointer" : "not-allowed", fontFamily: "inherit",
            }}>Log dose</button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
